import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { PASSWORD_RULES } from '@/features/auth/password';
import { useResetPassword } from '@/features/auth/hooks';
import { resetPasswordSchema, type ResetPasswordForm as ResetForm } from '@/schemas/password-reset';
import { AuthShell, AuthAlert, AuthButton, AuthField, AuthInput, FieldError, PasswordWrap, type AuthAlertState } from './login';

const searchSchema = z.object({ token: z.string().optional().catch(undefined) });

export const Route = createFileRoute('/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  return (
    <AuthShell>
      <h1 className="text-navy text-[34px] leading-tight m-0 mb-1.5" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        Choose a new password
      </h1>
      {token ? (
        <ResetPasswordForm token={token} />
      ) : (
        <>
          <AuthAlert alert={{ kind: 'error', text: 'This reset link is invalid or has expired.' }} />
          <p className="mt-[22px] text-sm text-muted text-center m-0">
            <Link to="/forgot-password" className="text-brand-blue font-semibold no-underline hover:underline">Request a new link</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}

export function ResetPasswordForm({ token }: { token: string }) {
  const reset = useResetPassword();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [alert, setAlert] = useState<AuthAlertState>(null);
  const form = useForm<ResetForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { new_password: '', confirm_password: '' },
    mode: 'onSubmit',
  });
  const pw = form.watch('new_password');

  const onSubmit = async (values: ResetForm) => {
    setAlert(null);
    try {
      await reset.mutateAsync({ token, new_password: values.new_password, confirm_password: values.confirm_password });
      void navigate({ to: '/login', search: { reset: '1' } });
    } catch (err) {
      setAlert({ kind: 'error', text: err instanceof ApiError ? err.message : 'Could not reset your password.' });
    }
  };

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
      <AuthAlert alert={alert} />
      <AuthField label="New password" htmlFor="new-password" required>
        <PasswordWrap show={showPw} onToggle={() => setShowPw((s) => !s)}>
          <AuthInput id="new-password" type={showPw ? 'text' : 'password'} autoComplete="new-password" {...form.register('new_password')} />
        </PasswordWrap>
        <ul className="mt-2 list-none p-0 m-0 grid grid-cols-1 gap-1 text-[12px]">
          {PASSWORD_RULES.map((rule) => {
            const ok = pw ? rule.test(pw) : false;
            return (
              <li key={rule.id} className={cn('flex items-center gap-2 leading-tight', ok ? 'text-state-success-text' : 'text-muted')}>
                <span aria-hidden className={cn('inline-block size-[10px] rounded-full border', ok ? 'bg-state-success-accent border-state-success-accent' : 'bg-transparent border-line-strong')} />
                <span>{rule.label}</span>
              </li>
            );
          })}
        </ul>
        <FieldError message={form.formState.errors.new_password?.message} />
      </AuthField>
      <AuthField label="Confirm new password" htmlFor="confirm-password" required>
        <AuthInput id="confirm-password" type={showPw ? 'text' : 'password'} autoComplete="new-password" {...form.register('confirm_password')} />
        <FieldError message={form.formState.errors.confirm_password?.message} />
      </AuthField>
      <AuthButton type="submit" disabled={reset.isPending}>
        {reset.isPending ? 'Resetting…' : 'Reset password'}
      </AuthButton>
    </form>
  );
}
